<div class="shell" [class.shell--nav-collapsed]="sidebarCollapsed()">
  <header class="shell__header">
    <div class="shell__brand">
      <span class="shell__product">Inventory</span>
      @if (tenantId(); as tid) {
        <span class="shell__tenant-name" [title]="tid">{{ tid | tenantName }}</span>
      }
    </div>

    <div class="shell__trail">
      <button
        type="button"
        class="shell__theme"
        [attr.aria-pressed]="isDark()"
        [attr.aria-label]="isDark() ? 'Switch to light mode' : 'Switch to dark mode'"
        [title]="isDark() ? 'Light mode' : 'Dark mode'"
        (click)="toggleTheme()"
      >
        {{ isDark() ? 'Light' : 'Dark' }}
      </button>
      <details class="shell__meta" data-rind-dismiss-outside>
        <summary>
          <span class="shell__meta-summary">
            @if (email(); as e) {
              {{ e }}
            } @else {
              Session
            }
          </span>
          <span
            class="shell__api-dot"
            [attr.data-status]="apiStatus()"
            [attr.title]="'API ' + apiStatus()"
            aria-hidden="true"
          ></span>
        </summary>
        <div class="shell__meta-panel">
          @if (tenantId(); as tid) {
            <div class="shell__meta-block">
              <div class="muted">Tenant ID</div>
              <code class="selectable">{{ tid }}</code>
            </div>
            <div class="shell__meta-block">
              <div class="muted">Location Code</div>
              @if (locationCode(); as code) {
                <code class="selectable">{{ code }}</code>
              } @else {
                <span class="muted">—</span>
              }
            </div>
            <p class="muted shell__meta-note">
              One Inventory tenant per browser. Signing into another tenant replaces this session.
            </p>
          }
          <div class="shell__meta-row">
            <span class="muted">API</span>
            <span
              class="status-pill"
              [attr.data-status]="apiStatus()"
              [attr.aria-label]="'API status: ' + apiStatus()"
            >
              {{ apiStatus() }}
            </span>
          </div>
          <div class="shell__meta-actions">
            <button type="button" (click)="logout()">Sign out</button>
          </div>
        </div>
      </details>
    </div>
  </header>

  <nav
    class="shell__nav"
    aria-label="Primary"
    [attr.aria-expanded]="!sidebarCollapsed()"
  >
    <button
      type="button"
      class="shell__nav-toggle"
      [attr.aria-expanded]="!sidebarCollapsed()"
      [attr.aria-label]="sidebarCollapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
      [title]="sidebarCollapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
      (click)="toggleSidebar()"
    >
      <span aria-hidden="true">{{ sidebarCollapsed() ? '»' : '«' }}</span>
    </button>

    <div class="shell__nav-body">
    @if (showOperations()) {
      <section
        class="shell__nav-group"
        [class.shell__nav-group--active]="isSectionActive('operations')"
        [class.shell__nav-group--collapsed]="!isExpanded('operations')"
      >
        <button
          type="button"
          class="shell__nav-heading"
          [attr.aria-expanded]="isExpanded('operations')"
          (click)="toggleSection('operations')"
        >
          <span class="shell__nav-chevron" aria-hidden="true">{{
            isExpanded('operations') ? '▾' : '▸'
          }}</span>
          <span class="shell__nav-label">Operations</span>
        </button>
        @if (isExpanded('operations')) {
          <div class="shell__nav-links">
            @if (showSkus()) {
              <a routerLink="/skus" routerLinkActive="active" ariaCurrentWhenActive="page">SKUs</a>
            }
            @if (showDeliveries()) {
              <a routerLink="/deliveries" routerLinkActive="active" ariaCurrentWhenActive="page"
                >Deliveries</a
              >
            }
          </div>
        }
      </section>
    }

    <section
      class="shell__nav-group"
      [class.shell__nav-group--active]="isSectionActive('reporting')"
      [class.shell__nav-group--collapsed]="!isExpanded('reporting')"
    >
      <button
        type="button"
        class="shell__nav-heading"
        [attr.aria-expanded]="isExpanded('reporting')"
        (click)="toggleSection('reporting')"
      >
        <span class="shell__nav-chevron" aria-hidden="true">{{
          isExpanded('reporting') ? '▾' : '▸'
        }}</span>
        <span class="shell__nav-label">Reporting</span>
      </button>
      @if (isExpanded('reporting')) {
        <div class="shell__nav-links">
          @if (showReports()) {
            <a
              routerLink="/reports/skus-printed"
              routerLinkActive="active"
              ariaCurrentWhenActive="page"
              >SKUs printed</a
            >
          } @else {
            <span class="muted shell__nav-empty">No permitted reports</span>
          }
        </div>
      }
    </section>

    @if (showConfiguration()) {
      <section
        class="shell__nav-group"
        [class.shell__nav-group--active]="isSectionActive('configuration')"
        [class.shell__nav-group--collapsed]="!isExpanded('configuration')"
      >
        <button
          type="button"
          class="shell__nav-heading"
          [attr.aria-expanded]="isExpanded('configuration')"
          (click)="toggleSection('configuration')"
        >
          <span class="shell__nav-chevron" aria-hidden="true">{{
            isExpanded('configuration') ? '▾' : '▸'
          }}</span>
          <span class="shell__nav-label">Configuration</span>
        </button>
        @if (isExpanded('configuration')) {
          <div class="shell__nav-links">
            @if (showProductsGroup()) {
              <div
                class="shell__nav-branch"
                [class.shell__nav-branch--active]="isSectionActive('products')"
                [class.shell__nav-branch--collapsed]="!isExpanded('products')"
              >
                <button
                  type="button"
                  class="shell__nav-heading"
                  [attr.aria-expanded]="isExpanded('products')"
                  (click)="toggleSection('products')"
                >
                  <span class="shell__nav-chevron" aria-hidden="true">{{
                    isExpanded('products') ? '▾' : '▸'
                  }}</span>
                  <span class="shell__nav-label">Products</span>
                </button>
                @if (isExpanded('products')) {
                  <div class="shell__nav-sublinks">
                    @if (showProducts()) {
                      <a
                        routerLink="/products/list"
                        routerLinkActive="active"
                        [routerLinkActiveOptions]="{ exact: false }"
                        ariaCurrentWhenActive="page"
                        >Products</a
                      >
                    }
                    @if (showContainers()) {
                      <a
                        routerLink="/products/containers"
                        routerLinkActive="active"
                        [routerLinkActiveOptions]="{ exact: false }"
                        ariaCurrentWhenActive="page"
                        >Container Library</a
                      >
                    }
                  </div>
                }
              </div>
            }
            @if (showStations()) {
              <a routerLink="/stations" routerLinkActive="active" ariaCurrentWhenActive="page"
                >Stations</a
              >
            }
            @if (showOperators()) {
              <a routerLink="/people" routerLinkActive="active" ariaCurrentWhenActive="page"
                >People</a
              >
            }
            @if (showTradePartners()) {
              <div
                class="shell__nav-branch"
                [class.shell__nav-branch--active]="isSectionActive('trade-partners')"
                [class.shell__nav-branch--collapsed]="!isExpanded('trade-partners')"
              >
                <button
                  type="button"
                  class="shell__nav-heading"
                  [attr.aria-expanded]="isExpanded('trade-partners')"
                  (click)="toggleSection('trade-partners')"
                >
                  <span class="shell__nav-chevron" aria-hidden="true">{{
                    isExpanded('trade-partners') ? '▾' : '▸'
                  }}</span>
                  <span class="shell__nav-label">Trade partners</span>
                </button>
                @if (isExpanded('trade-partners')) {
                  <div class="shell__nav-sublinks">
                    <a
                      routerLink="/trade-partners/list"
                      routerLinkActive="active"
                      [routerLinkActiveOptions]="{ exact: false }"
                      ariaCurrentWhenActive="page"
                      >List</a
                    >
                    <a
                      routerLink="/trade-partners/pricing"
                      routerLinkActive="active"
                      [routerLinkActiveOptions]="{ exact: false }"
                      ariaCurrentWhenActive="page"
                      >Price mapping</a
                    >
                    <a
                      routerLink="/trade-partners/mappings"
                      routerLinkActive="active"
                      [routerLinkActiveOptions]="{ exact: false }"
                      ariaCurrentWhenActive="page"
                      >Product mapping</a
                    >
                  </div>
                }
              </div>
            }
          </div>
        }
      </section>
    }

    @if (!hasAnyFeature()) {
      <span class="muted shell__nav-empty">No permitted features</span>
    }
    </div>
  </nav>

  <main class="shell__main">
    <router-outlet />
  </main>
</div>
